import type { KeyboardEvent, ReactNode } from "react";
import { useIntl } from "react-intl";

// Canonical preview tile for an uploaded/attached file (@adr SPARK-0022-file-drop-fields@2). One look for
// in-memory upload previews across the app (Match challenge, Share create,
// extraction): a square card with an image thumbnail when available, otherwise
// a neutral file glyph, plus name + size, an optional corner badge, and a
// hover remove button. (ServicePage's DocPreviewCard is the richer per-document
// card backed by genesis-generated thumbnails; this tile is for fresh uploads.)

function formatSize(bytes?: number | null): string {
  if (!bytes) return "";
  const kb = Math.round(bytes / 1024);
  return kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export function FilePreviewTile({
  idPrefix,
  name,
  sizeBytes,
  previewUrl,
  badge,
  selected = false,
  onRemove,
  onClick,
  title,
}: {
  /** Testanker; unbesetzt, wo kein Test hingreift. */
  idPrefix?: string;
  name: string;
  sizeBytes?: number | null;
  /** Image thumbnail URL; falls back to a file glyph when absent. */
  previewUrl?: string | null;
  /** Corner badge, e.g. a "preview image" marker. */
  badge?: ReactNode;
  selected?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  title?: string;
}) {
  const intl = useIntl();
  const size = formatSize(sizeBytes);
  const interactiveProps = onClick
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": title ?? name,
        "aria-pressed": selected,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      onClick={onClick}
      title={title}
      {...interactiveProps}
      data-state-mark-position="start"
      className={`bg-page box-flat group relative overflow-hidden transition ${
        selected ? "smycles-selected ring-2 ring-accent-decoration" : "ring-1 ring-ink/5"
      } ${onClick ? "cursor-pointer hover:ring-accent-decoration/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui" : ""}`}
    >
      {onRemove ? (
        <button
          id={idPrefix && `${idPrefix}-remove`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
          aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
          className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-smycles-micro text-ink opacity-0 transition group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100"
        >
          ✕
        </button>
      ) : null}
      {previewUrl ? (
        <div className="aspect-[4/3] overflow-hidden bg-ink/[0.06]">
          <img src={previewUrl} alt={name} className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="flex aspect-[4/3] items-center justify-center text-ink-muted">
          <svg viewBox="0 0 24 24" className="h-9 w-9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
            <path d="M5 3h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
          </svg>
        </div>
      )}
      {badge ? <div className="absolute bottom-1 left-1">{badge}</div> : null}
      <div className="px-2 py-1.5">
        <div id={idPrefix && `${idPrefix}-name`} className="truncate text-smycles-label font-medium">{name}</div>
        {size ? <div className="text-smycles-micro text-ink-muted">{size}</div> : null}
      </div>
    </div>
  );
}
